import React from 'react'
import PropTypes from 'prop-types'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { formatISO } from 'date-fns'
import { IonButton } from '@ionic/react'
import { useSubmitMessages } from 'utils'
import {
  ADD_TECH_COMMENTS_ACTION,
  STALE_WORK_ORDER_MESSAGE,
  TECH_COMMENTS_SAVED_MESSAGE,
} from '../constants'
import {
  IN_PROGRESS_STATUSES,
  getInProgressActionOptions,
  getNewInProgressStatus,
  isStaleWorkOrderError,
} from '../specialActions'
import EtaResetFields from './EtaResetFields'
import SectionTextarea from './SectionTextarea'
import AddTechCommentsFields, {
  techCommentsValidation,
} from './AddTechCommentsFields'

function getInitialValues() {
  return {
    in_progress_action: '',
    eta_date: formatISO(new Date(), { representation: 'date' }),
    eta_time: 'morning',
    tech_comments: '',
  }
}

const validationSchema = Yup.object({
  tech_comments: techCommentsValidation('in_progress_action'),
  eta_date: Yup.string().when('in_progress_action', {
    is: 'reset_eta',
    then: (schema) => schema.required('Please enter your new ETA date'),
    otherwise: (schema) => schema,
  }),
  eta_time: Yup.string().when('in_progress_action', {
    is: 'reset_eta',
    then: (schema) => schema.required('Please enter your new ETA time'),
    otherwise: (schema) => schema,
  }),
})

function InProgressActionsForm({
  currentStatus,
  canResetEta,
  onResetEta,
  onChangeStatus,
  onAddTechComments,
  onRefreshWorkOrder,
}) {
  const { displaySubmitSuccess, displaySubmitFailure } = useSubmitMessages()
  const newStatus = getNewInProgressStatus(currentStatus)
  const actionOptions = getInProgressActionOptions({ canResetEta, newStatus })

  return (
    <Formik
      initialValues={getInitialValues()}
      validationSchema={validationSchema}
      onSubmit={async (values, { resetForm }) => {
        const resetToInitial = () => resetForm({ values: getInitialValues() })

        try {
          if (values.in_progress_action === 'reset_eta') {
            await onResetEta({
              eta_date: values.eta_date,
              eta_time: values.eta_time,
              tech_comments: values.tech_comments,
            })
            displaySubmitSuccess('The work order was rescheduled successfully.')
            resetToInitial()
            return
          }

          if (values.in_progress_action === 'change_status') {
            await onChangeStatus({
              order_status: newStatus,
              tech_comments: values.tech_comments,
            })
            displaySubmitSuccess(
              `The work order status was changed to ${newStatus}.`
            )
            resetToInitial()
            return
          }

          if (values.in_progress_action === ADD_TECH_COMMENTS_ACTION) {
            await onAddTechComments({ tech_comments: values.tech_comments })
            displaySubmitSuccess(TECH_COMMENTS_SAVED_MESSAGE)
            resetToInitial()
          }
        } catch (error) {
          // The page was showing a stale status (e.g. another manager already
          // changed it), so the option offered was no longer valid. Refetch so
          // the form offers the right option again.
          if (isStaleWorkOrderError(error)) {
            displaySubmitFailure({
              customFormWideError: STALE_WORK_ORDER_MESSAGE,
            })
            resetToInitial()
            onRefreshWorkOrder()
            return
          }
          displaySubmitFailure(error?.errors, null, error)
        }
      }}
      validateOnBlur={false}
    >
      {({
        values,
        setFieldValue,
        setFieldTouched,
        handleChange,
        handleBlur,
        errors,
        touched,
        isSubmitting,
      }) => (
        <Form className="accept-decline-section">
          <h3 className="accept-decline-section__title">In Progress Update</h3>
          <p className="accept-decline-section__hint">
            Your work order is in progress, but it may require an update. Please
            select from the below options to make your update.
          </p>
          <div className="accept-decline-section__field">
            <label
              className="accept-decline-section__label"
              htmlFor="in-progress-action"
            >
              Update Type
            </label>
            <select
              id="in-progress-action"
              name="in_progress_action"
              className="accept-decline-section__input-native"
              value={values.in_progress_action}
              onChange={(event) => {
                const nextValue = event.target.value
                setFieldValue('in_progress_action', nextValue)
                if (nextValue !== 'reset_eta') {
                  setFieldValue(
                    'eta_date',
                    formatISO(new Date(), { representation: 'date' })
                  )
                  setFieldValue('eta_time', 'morning')
                }
                // Every action owns its own tech comments entry; clear it when
                // switching so comments typed for one action don't ride along
                // with another.
                setFieldValue('tech_comments', '')
                setFieldTouched('tech_comments', false)
              }}
            >
              <option value="">Select an option</option>
              {actionOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {values.in_progress_action === 'reset_eta' && (
            <>
              <EtaResetFields
                idPrefix="in-progress-actions"
                values={values}
                errors={errors}
                touched={touched}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                handleChange={handleChange}
                handleBlur={handleBlur}
              />

              <SectionTextarea
                id="in-progress-tech-comments"
                name="tech_comments"
                label="Tech Comments"
                value={values.tech_comments}
                error={errors.tech_comments}
                touched={touched.tech_comments}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                required={false}
              />

              <IonButton
                type="submit"
                color="tss-grey-dark"
                className="accept-decline-section__btn"
                disabled={isSubmitting}
              >
                Submit
              </IonButton>
            </>
          )}

          {values.in_progress_action === 'change_status' && (
            <>
              <p className="accept-decline-section__hint">
                {`This will change the work order status from ${currentStatus} to ${newStatus}.`}
              </p>

              <SectionTextarea
                id="in-progress-change-status-tech-comments"
                name="tech_comments"
                label="Tech Comments"
                value={values.tech_comments}
                error={errors.tech_comments}
                touched={touched.tech_comments}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                required={false}
              />

              <IonButton
                type="submit"
                color="tss-grey-dark"
                className="accept-decline-section__btn"
                disabled={isSubmitting}
              >
                Submit
              </IonButton>
            </>
          )}

          {values.in_progress_action === ADD_TECH_COMMENTS_ACTION && (
            <AddTechCommentsFields
              idPrefix="in-progress-add"
              values={values}
              errors={errors}
              touched={touched}
              setFieldValue={setFieldValue}
              setFieldTouched={setFieldTouched}
              isSubmitting={isSubmitting}
            />
          )}
        </Form>
      )}
    </Formik>
  )
}

InProgressActionsForm.propTypes = {
  currentStatus: PropTypes.oneOf(IN_PROGRESS_STATUSES).isRequired,
  canResetEta: PropTypes.bool.isRequired,
  onResetEta: PropTypes.func.isRequired,
  onChangeStatus: PropTypes.func.isRequired,
  onAddTechComments: PropTypes.func.isRequired,
  onRefreshWorkOrder: PropTypes.func.isRequired,
}

export default InProgressActionsForm
